/**
 * Durable anonymous browser key: the customer-journey key (PIX-45) and the
 * LLM-referral visitor id.
 *
 * WHAT IT IS: a random UUID in localStorage. It identifies a BROWSER so its
 * journey (gate verification, free run, payment, paid audit, saved report, or
 * an LLM-referred landing) can be joined in Supabase. It is not an identity:
 * the key is linked to a hashed address only when the visitor verifies an
 * email, pays or runs a paid audit, and nothing derived from the person (IP,
 * fingerprint, email) ever feeds into the key itself.
 *
 * Consent-gated on the same central resolver GA4/Clarity/PostHog use: no key
 * is minted or returned unless analytics are currently allowed. AnalyticsGate
 * mints it when analytics becomes allowed for any visitor (D2 in
 * docs/radar-customer-journey-scope.md; it used to be minted only on LLM
 * landings), and on withdrawal it takes the stored key off the browser and
 * queues its server-side journey for deletion, retried until the server
 * confirms. A non-consenting visitor has no key and is never joined.
 *
 * Storage mirrors first-touch-utm.ts deliberately: one namespaced key, an
 * in-memory fallback so a blocked/unavailable localStorage never throws, and a
 * consent-aware read that clears rather than returning a stale id.
 *
 * PostHog's `distinct_id` is deliberately NOT reused, and this key is never
 * sent to PostHog (posthog-client.ts strips any `visitor` property). That
 * client runs person_profiles:'identified_only' and never calls identify();
 * the journey lives in our database, which keeps the cookie-policy promise
 * that we never tell PostHog who you are.
 */

import { isAnalyticsAllowed } from '@/lib/consent-manager'

const STORAGE_KEY = 'pm_visitor_id'

// Canonical v4 UUID. Anything else in storage is treated as corrupt and
// replaced, so a hand-edited or truncated value cannot reach the API.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

let memoryId: string | null = null

/**
 * Consent check that can never throw.
 *
 * These functions are called INLINE while building a contact payload, an audit
 * request and a Paddle checkout. If the consent resolver ever threw, an
 * unguarded call would take the conversion down with it — and attribution must
 * never block a conversion. Failing closed (treat as not allowed) is the safe
 * direction: worst case an attribution row is missed.
 */
function consentAllowed(): boolean {
  try {
    return isAnalyticsAllowed() === true
  } catch {
    return false
  }
}

function safeGet(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function safeSet(value: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Held in memory for this session only.
  }
}

function mintUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
  } catch {
    // Fall through to the manual path.
  }
  // Fallback for older/embedded contexts without randomUUID.
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * The current visitor id, minting one on first consented call.
 *
 * Returns null when analytics are not allowed — an explicit denial, an
 * undecided prior-consent region, or a lapsed consent record — and clears any
 * existing id so stale attribution can never be read or forwarded.
 */
export function getOrCreateVisitorId(): string | null {
  if (typeof window === 'undefined') return null
  if (!consentAllowed()) {
    clearVisitorId()
    return null
  }

  const stored = safeGet()
  if (stored && UUID_RE.test(stored)) return stored
  if (memoryId && UUID_RE.test(memoryId)) return memoryId

  try {
    const minted = mintUuid()
    memoryId = minted
    safeSet(minted)
    return minted
  } catch {
    // No crypto available — attribution is skipped, never fatal.
    return null
  }
}

/**
 * The visitor id WITHOUT minting one. For conversion call sites that should
 * attribute an existing visitor but must never create attribution as a side
 * effect of a form submit or a checkout.
 */
export function peekVisitorId(): string | null {
  if (typeof window === 'undefined') return null
  if (!consentAllowed()) return null
  const stored = safeGet()
  if (stored && UUID_RE.test(stored)) return stored
  return memoryId && UUID_RE.test(memoryId) ? memoryId : null
}

/** Called on consent withdrawal. */
export function clearVisitorId(): void {
  memoryId = null
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // no-op
  }
}

/**
 * Headers carrying the key on a first-party API call, or none without one.
 * The ONE place the client names the header, so the server's
 * VISITOR_ID_HEADER (visitor-journey.ts) has a single counterpart.
 */
export function visitorIdHeaders(): Record<string, string> {
  const id = peekVisitorId()
  return id ? { 'x-pm-visitor-id': id } : {}
}

/**
 * The stored key, read WITHOUT the consent check, then cleared. Only for the
 * withdrawal path: peekVisitorId() already returns null once consent is gone,
 * but the server still holds that key's journey and has to be told to revoke
 * it. Returns null when there is nothing to revoke, so a visitor who never
 * consented triggers no request.
 */
export function takeVisitorIdForRevocation(): string | null {
  const stored = safeGet()
  const candidate =
    stored && UUID_RE.test(stored)
      ? stored
      : memoryId && UUID_RE.test(memoryId)
        ? memoryId
        : null
  clearVisitorId()
  return candidate
}

const REVOKE_URL = '/api/visitor-journey/revoke'

// Keys whose server-side journey still has to be deleted. Held ONLY to finish
// a withdrawal the visitor asked for: written when a key is taken for
// revocation, removed once the server confirms, never read for anything else.
// It exists because the request can be lost (offline, blocked, the tab
// closing), and clearing the only copy first would leave the server-side link
// in place until retention, breaking the withdrawal promise.
const REVOKE_PENDING_KEY = 'pm_visitor_revoke_pending'

function readPending(): string[] {
  try {
    const raw = localStorage.getItem(REVOKE_PENDING_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter(
          (v): v is string => typeof v === 'string' && UUID_RE.test(v)
        )
      : []
  } catch {
    return []
  }
}

function writePending(keys: string[]): void {
  try {
    if (keys.length === 0) localStorage.removeItem(REVOKE_PENDING_KEY)
    else localStorage.setItem(REVOKE_PENDING_KEY, JSON.stringify(keys))
  } catch {
    // Blocked storage: the direct attempt in flushPendingRevocations is the
    // only one this key gets.
  }
}

/**
 * Send every pending revocation, plus `alsoRevoke` when given. A key leaves
 * the queue only when the server answers ok, or 400 (a malformed key can never
 * succeed); rate limits, server errors and network failures keep it for the
 * next page load. Nothing is ever dropped to make room: the queue only grows
 * when a withdrawal has not yet been confirmed, and each key is a short UUID.
 * Idempotent on the server, so a retry after a lost response is harmless.
 *
 * `alsoRevoke` is sent even if it never made it into the queue, so a key that
 * lived only in memory (browser storage blocked) is still revoked once.
 */
export function flushPendingRevocations(alsoRevoke?: string | null): void {
  const keys = new Set(readPending())
  if (alsoRevoke && UUID_RE.test(alsoRevoke)) keys.add(alsoRevoke)
  for (const visitorId of keys) {
    try {
      void fetch(REVOKE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId }),
        keepalive: true,
      })
        .then(res => {
          if (res.ok || res.status === 400) {
            writePending(readPending().filter(k => k !== visitorId))
          }
        })
        .catch(() => undefined)
    } catch {
      // Best-effort; retried on the next load.
    }
  }
}

/**
 * Consent withdrawal (and silent consent expiry): take the stored key off the
 * browser, queue its server-side journey for deletion, and send the queue and
 * the key itself. No request fires when there is nothing to revoke.
 */
export function revokeStoredVisitorJourney(): void {
  const taken = takeVisitorIdForRevocation()
  if (taken) {
    writePending([...readPending().filter(k => k !== taken), taken])
  }
  flushPendingRevocations(taken)
}
